/**
 * TenantLoader — premium light-theme loader for tenant public pages.
 *
 * Designed to feel deliberate and editorial, not generic. Animates a
 * pulsing wordmark frame + horizontal scan line + tagline shimmer.
 * No external API calls, no logo flash — paints instantly on mount.
 */

import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';

export default function TenantLoader({ slug, label = 'Loading' }) {
    const reduced = useReducedMotion();
    // Derive a friendly title from the slug as fallback wordmark.
    const title = slug
        ? slug.replace(/[-_]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
        : '';

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#f8f6f0] text-[#0c1a12] px-6 overflow-hidden">
            {/* faint grain */}
            <div
                aria-hidden
                className="absolute inset-0 opacity-[0.04]"
                style={{
                    backgroundImage:
                        'radial-gradient(circle at 25% 25%, #0c1a12 1px, transparent 1px), radial-gradient(circle at 75% 75%, #0c1a12 1px, transparent 1px)',
                    backgroundSize: '6px 6px, 9px 9px'
                }}
            />
            {/* corner brackets — premium editorial framing */}
            <Bracket pos="top-left" />
            <Bracket pos="top-right" />
            <Bracket pos="bottom-left" />
            <Bracket pos="bottom-right" />

            <div className="relative flex flex-col items-center gap-6 max-w-md text-center">
                {/* Top label */}
                <motion.div
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="text-[10px] uppercase tracking-[0.6em] text-[#0c1a12]/50"
                >
                    Workshop ·
                    <span className="ml-1.5">{label}</span>
                </motion.div>

                {/* Big wordmark */}
                {title && (
                    <motion.h1
                        initial={{ opacity: 0, letterSpacing: '0.5em' }}
                        animate={{ opacity: 1, letterSpacing: '0.18em' }}
                        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                        className="font-display text-3xl sm:text-5xl tracking-[0.18em] uppercase"
                    >
                        {title}
                    </motion.h1>
                )}

                {/* Animated scan line */}
                <div className="relative w-44 h-px bg-[#0c1a12]/15 overflow-hidden">
                    {!reduced && (
                        <motion.div
                            className="absolute inset-y-0 w-1/3 bg-[#0c1a12]"
                            initial={{ x: '-100%' }}
                            animate={{ x: '300%' }}
                            transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
                        />
                    )}
                </div>

                {/* Subtle tagline */}
                <div className="text-[11px] uppercase tracking-[0.4em] text-[#0c1a12]/40">
                    Honest mechanics · Master finish
                </div>
            </div>
        </div>
    );
}

function Bracket({ pos }) {
    const styles = {
        'top-left': 'top-6 left-6 border-l border-t',
        'top-right': 'top-6 right-6 border-r border-t',
        'bottom-left': 'bottom-6 left-6 border-l border-b',
        'bottom-right': 'bottom-6 right-6 border-r border-b'
    }[pos];
    return (
        <span aria-hidden className={`absolute w-8 h-8 border-[#0c1a12]/30 ${styles}`} />
    );
}
